import type { PoolShippingDetail } from '../../lib/api/operatorConsole';
import { formatAmount } from './formatMoney';
import { ShippingPromoLines } from './ShippingPromoLines';

interface Props {
  shipping: PoolShippingDetail;
}

const LINES: { key: 'shipping' | 'insurance' | 'signature' | 'handling' | 'sales_tax'; label: string }[] = [
  { key: 'shipping', label: 'Shipping fee' },
  { key: 'insurance', label: 'Insurance' },
  { key: 'signature', label: 'Signature' },
  { key: 'handling', label: 'Handling fee' },
  { key: 'sales_tax', label: 'Sales tax' },
];

// Yes / no rather than raw booleans: "insurance false" reads as an error,
// "Insurance: no" reads as a choice.
function yesNo(value: unknown): string {
  if (value === true) return 'yes';
  if (value === false) return 'no';
  return '—';
}

function chosenLine(chosen: Record<string, unknown> | null): string {
  if (!chosen) return '—';

  return `insurance ${yesNo(chosen['insurance'])}, signature ${yesNo(chosen['signature'])}`;
}

export function ShippingFeesPanel({ shipping }: Props) {
  const { fees, protection } = shipping;

  return (
    <section
      aria-label="Fees and protection"
      className="grid gap-4 md:grid-cols-2"
    >
      <div className="rounded-lg border border-ui-border bg-ui-card p-4">
        <h2 className="text-sm font-semibold text-ui-text">What the player paid</h2>
        {!fees ? (
          <p className="mt-2 text-sm text-ui-text-secondary">
            No charge breakdown was recorded for this delivery.
          </p>
        ) : (
          <dl className="mt-2 space-y-1 text-sm">
            {LINES.map(({ key, label }) => (
              <div key={key} className="flex justify-between gap-4">
                <dt className="text-ui-text-secondary">{label}</dt>
                <dd className="font-mono text-ui-text">
                  {formatAmount(fees[key], shipping.currency)}
                </dd>
              </div>
            ))}
            <div className="flex justify-between gap-4 border-t border-ui-border pt-1 font-semibold">
              <dt className="text-ui-text">Total</dt>
              <dd className="font-mono text-ui-text">
                {formatAmount(fees.total, shipping.currency)}
              </dd>
            </div>
          </dl>
        )}
        {fees?.promo && <ShippingPromoLines promo={fees.promo} currency={shipping.currency} />}
      </div>

      <div className="rounded-lg border border-ui-border bg-ui-card p-4">
        <h2 className="text-sm font-semibold text-ui-text">Protection the player chose</h2>
        {!protection ? (
          <p className="mt-2 text-sm text-ui-text-secondary">
            No protection choice was recorded for this delivery.
          </p>
        ) : (
          <dl className="mt-2 space-y-1 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-ui-text-secondary">Chosen</dt>
              <dd className="text-ui-text">{chosenLine(protection.chosen)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-ui-text-secondary">Required</dt>
              <dd className="text-ui-text">{chosenLine(protection.required)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-ui-text-secondary">Declined</dt>
              <dd className="text-ui-text">
                {protection.opted_out
                  ? `yes${protection.opted_out_of.length ? ` (${protection.opted_out_of.join(', ')})` : ''}`
                  : 'no'}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-ui-text-secondary">Acknowledged</dt>
              <dd className="text-ui-text">{yesNo(protection.acknowledged)}</dd>
            </div>
            {/* Both are caps we apply, not player choices — support gets asked
                about them when a claim is short. */}
            <div className="flex justify-between gap-4">
              <dt className="text-ui-text-secondary">Single card over max</dt>
              <dd className="text-ui-text">{yesNo(protection.single_card_over_max)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-ui-text-secondary">Insurance capped</dt>
              <dd className="text-ui-text">{yesNo(protection.insurance_capped)}</dd>
            </div>
          </dl>
        )}
      </div>
    </section>
  );
}
